import React, {ReactElement, useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {menuEditorActions, menuEditorSelectors} from "../editorSlice";
import {EditorPreviewPage} from "./EditorPreviewPage";
import {CoverPagePreview} from "./CoverPagePreview";
import {EmptyPreview} from "./EmptyPreview";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    MeasuringStrategy, MouseSensor, TouchSensor, useSensor, useSensors
} from "@dnd-kit/core";
import {restrictToFirstScrollableAncestor, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {dndIdToItemKey, dndMove, DndState, documentToDndState} from "./dragndrop";
import {PreviewItem} from "./PreviewItem";
import { createDocumentHandler } from "../documentHandlerUtils";

export interface EditorPreviewProps {

}

export function EditorPreview(props: EditorPreviewProps): ReactElement | null {
    const dispatch = useAppDispatch();
    const document = useAppSelector(menuEditorSelectors.document);
    const [dndState, setDndState] = useState<DndState | null>(null);
    const draggingItem = useAppSelector(menuEditorSelectors.draggingItem);

    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5
            }
        })
    );

    useEffect(() => {
        if (document != null) {
            setDndState(documentToDndState(document));
        }
    }, [document]);

    if (document == null || dndState == null) {
        return null;
    }
    const docHandler = createDocumentHandler(document);

    const unfocus = () => dispatch(menuEditorActions.setFocusedItem(null));

    const handleDragStart = (e: DragStartEvent) => {
        const itemKey = dndIdToItemKey(e.active.id);
        dispatch(menuEditorActions.setDraggingItem(itemKey));
    }

    const handleDragOver = (e: DragOverEvent) => {
        const {active, over} = e;
        if (over != null && active.id !== over.id) {
            const newState = dndMove(dndState, active, over);
            if (newState != null) {
                setDndState(newState);
            }
        }
    };

    const handleDragEnd = (e: DragEndEvent) => {
        const {active, over} = e;
        if (over != null && active.id !== over.id) {
            const oldKey = dndIdToItemKey(active.id);
            const newKey = dndIdToItemKey(over?.id);
            dispatch(menuEditorActions.moveItem({old: oldKey, new: newKey}));
        }
    };

    const pageWrapperStyling = docHandler.getEditablePageWrapperStyling();

    return (
        <DndContext
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            measuring={{
                droppable: {
                    strategy: MeasuringStrategy.Always
                }
            }}
            modifiers={[
                restrictToVerticalAxis,
                restrictToFirstScrollableAncestor
            ]}
            sensors={sensors}
        >
            <div className={`flex flex-col items-center
                             ${pageWrapperStyling.className}`}
                             style={pageWrapperStyling.style}
                 onClick={unfocus}
            >
                {document.coverPage && <CoverPagePreview/>}

                {document.pages.map((page, i) => (
                    <EditorPreviewPage
                        key={i}
                        page={{key: i, page: page}}
                        format={document.format}
                        degu={document.degu || false}
                        displayFooter={document.displayFooter}
                        dndItems={dndState[i]}
                        isPreview={true}
                    />
                ))}

                {document.pages.length === 0 && <EmptyPreview/>}
            </div>
            <DragOverlay
                adjustScale={false}
                dropAnimation={{duration: 0, easing: 'linear', dragSourceOpacity: 0}}>
                {draggingItem && (
                    <div className="bg-white rounded shadow-2xl py-2 px-3">
                        <PreviewItem item={draggingItem} format={document.format}/>
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    );
}
